import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { toast } from 'react-hot-toast';

import {
    ShoppingCart, Phone, Instagram,
    CreditCard, Store, Clock, Trash2, Plus, Minus,
    X, Truck, Package, MapPin, Map, Navigation,
    Search, ArrowRight, ShoppingBag, ChefHat,
    ChevronLeft, ChevronRight, ChevronDown, AlignLeft,
    Tag, ArrowDownUp
} from 'lucide-react';
import { buildBrandOptions, normalizeBrandKey, normalizeBrandLabel } from '../utils/brandFilters';

import ProductDetailModal from '../components/tienda/ProductDetailModal';
import CheckoutModal from '../components/tienda/CheckoutModal';
import LowStockNotice from '../components/tienda/LowStockNotice';
import { getLowStockAlertConfig, getLowStockCartItems } from '../utils/storeLowStockAlert';
import { isStoreOrderFromWeb } from '../utils/storeLogistics';
import { resolveStoreImageUrl } from '../utils/storeImages';
import {
    getCleanVariantLabel,
    sanitizeProductDisplayName,
} from '../utils/productLabels';
import {
    getPriceListAdjustedBasePrice,
    getPriceListAdjustedVariantPrice,
} from '../utils/priceLists';

const STOREFRONT_REFRESH_EVENT = 'impulza:storefront-refresh';
const STOREFRONT_REFRESH_STORAGE_KEY = 'impulza:storefront-refresh';
const STORE_PRICE_ACCESS_STORAGE_PREFIX = 'impulza:store-price-access';

// --- UTILS ---
const formatCurrency = (amount) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(amount);
const normalizeDocument = (value) => String(value || '').replace(/\D/g, '');
const buildStorePriceAccessKey = (slug, organizationId) => `${STORE_PRICE_ACCESS_STORAGE_PREFIX}:${organizationId || 'public'}:${String(slug || '').toLowerCase()}`;

const STATUS_LABELS = {
  recibido: 'Nuevo',
  en_preparacion: 'Preparando',
  listo: 'Listo',
  en_camino: 'En Camino',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

const getStatusColor = (status) => {
    switch(status) {
        case 'entregado': return 'bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] border-[color:var(--color-accent)]';
        case 'cancelado': return 'bg-rose-100 text-rose-700 border-rose-200';
        case 'en_camino': return 'bg-orange-100 text-orange-700 border-orange-200';
        case 'listo': return 'bg-purple-100 text-purple-700 border-purple-200';
        case 'en_preparacion': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        default: return 'bg-blue-100 text-mint-700 border-blue-200';
    }
};

const withAlpha = (color, alphaHex) => {
    if (typeof color !== 'string') return color;
    const value = color.trim();
    if (/^#([0-9a-fA-F]{6})$/.test(value)) return `${value}${alphaHex}`;
    if (/^#([0-9a-fA-F]{3})$/.test(value)) {
        const [r, g, b] = value.slice(1).split('');
        return `#${r}${r}${g}${g}${b}${b}${alphaHex}`;
    }
    return value;
};

const checkStoreStatus = (openHours, alwaysOpen) => {
    if (alwaysOpen) return { isOpen: true, message: 'Abierto 24/7' };
    if (!openHours || !openHours.weekly) return { isOpen: true, message: 'Consultar Horario' };

    const now = new Date();
    const daysKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const currentKey = daysKeys[now.getDay()];

    const currentHour = now.getHours();
    const currentMin = now.getMinutes();
    const currentTimeVal = currentHour * 60 + currentMin;

    const ranges = openHours.weekly[currentKey] || [];

    if (ranges.length === 0) return { isOpen: false, message: 'Cerrado hoy' };

    for (let range of ranges) {
        if (!range.from || !range.to) continue;
        const [openH, openM] = range.from.split(':').map(Number);
        const [closeH, closeM] = range.to.split(':').map(Number);

        const startVal = openH * 60 + openM;
        const endVal = closeH * 60 + closeM;

        if (currentTimeVal >= startVal && currentTimeVal < endVal) {
            return { isOpen: true, message: `Abierto hasta las ${range.to}` };
        }
    }

    return { isOpen: false, message: 'Cerrado ahora' };
};

/**
 * Tienda.jsx - Frontend Público Integrado al ERP
 * Lógica robusta de inserción en DB (Clients -> Sales -> Items -> Order)
 */
export default function Tienda() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const welcomePopupShownRef = useRef(false);

    // Carrusel state
    const [currentSlide, setCurrentSlide] = useState(0);
    // Slides que el navegador no pudo cargar (URL que no es una imagen, archivo
    // borrado del bucket, host caído). Se descartan al renderizar.
    const [brokenSlides, setBrokenSlides] = useState([]);

    // Estados de Datos
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [rawProducts, setRawProducts] = useState([]);
    const [products, setProducts] = useState([]);
    const [priceLists, setPriceLists] = useState([]);
    const [siteId, setSiteId] = useState(null);
    const [activeStoreClient, setActiveStoreClient] = useState(null);

    // Estados de UI/UX
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [welcomePopupOpen, setWelcomePopupOpen] = useState(false);

    // Navegación (Tabs)
    const [activeTab, setActiveTab] = useState('productos');

    // Filtros
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Todos');
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState('todas');
    const [isBrandOpen, setIsBrandOpen] = useState(false);
    const [sortBy, setSortBy] = useState('relevancia');

    // Rastreo
    const [rastreoPhone, setRastreoPhone] = useState('');
    const [rastreoOrders, setRastreoOrders] = useState([]);
    const [rastreoLoading, setRastreoLoading] = useState(false);
    const [rastreoSearched, setRastreoSearched] = useState(false);

    // Métodos de Pago
    const [enabledPaymentMethods, setEnabledPaymentMethods] = useState([]);
    const [qtyDrafts, setQtyDrafts] = useState({});
    const [priceAccessCuit, setPriceAccessCuit] = useState('');
    const [priceAccessLoading, setPriceAccessLoading] = useState(false);
    const [priceAccessError, setPriceAccessError] = useState('');
    const refreshTimerRef = useRef(null);

    // Slides realmente mostrables: sin vacíos y sin los que fallaron al cargar.
    // Así un carrusel roto (URL que no es imagen, archivo borrado) no tapa el
    // banner: sin slides usables, el header cae a banner_url.
    const carouselSlides = useMemo(() => {
        if (!config?.theme_config?.carousel?.enabled) return [];
        return (config.theme_config.carousel.slides || [])
            .map((slide) => resolveStoreImageUrl(slide))
            .filter((url) => url && !brokenSlides.includes(url));
    }, [config?.theme_config?.carousel, brokenSlides]);

    // Si se cae un slide, el índice puede quedar fuera de rango.
    useEffect(() => {
        if (currentSlide >= carouselSlides.length) setCurrentSlide(0);
    }, [carouselSlides.length, currentSlide]);

    // --- EFECTOS EXTRAS ---
    useEffect(() => {
        if (carouselSlides.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentSlide(prev => (prev === carouselSlides.length - 1 ? 0 : prev + 1));
        }, 5000);
        return () => clearInterval(interval);
    }, [carouselSlides.length]);

    // Identidad en la pestaña del navegador: título + favicon propios de la tienda.
    // Se restauran al desmontar para no ensuciar el resto de la SPA.
    const storeTitle = config?.store_title;
    const storeFavicon = config?.favicon_url;
    useEffect(() => {
        if (!storeTitle && !storeFavicon) return;

        const previousTitle = document.title;
        if (storeTitle) document.title = storeTitle;

        const faviconUrl = resolveStoreImageUrl(storeFavicon);
        let link = null;
        let previousHref = null;
        if (faviconUrl) {
            link = document.querySelector("link[rel~='icon']");
            if (link) {
                previousHref = link.getAttribute('href');
            } else {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.setAttribute('href', faviconUrl);
        }

        return () => {
            document.title = previousTitle;
            if (link && previousHref !== null) link.setAttribute('href', previousHref);
        };
    }, [storeTitle, storeFavicon]);

    // --- CARGA INICIAL ---
    useEffect(() => {
        fetchStoreData();
        welcomePopupShownRef.current = false;
        setWelcomePopupOpen(false);
        setActiveStoreClient(null);
        setRawProducts([]);
        setProducts([]);
        setPriceLists([]);
        setPriceAccessCuit('');
        setPriceAccessError('');
        // eslint-disable-next-line
    }, [slug]);

    useEffect(() => {
        const popupConfig = config?.theme_config?.welcomePopup;
        if (!popupConfig?.enabled) return;
        if (welcomePopupShownRef.current) return;

        welcomePopupShownRef.current = true;
        setWelcomePopupOpen(true);
    }, [config?.theme_config?.welcomePopup]);

    const activePriceList = useMemo(
        () => priceLists.find((priceList) => priceList.id === activeStoreClient?.price_list_id) || null,
        [priceLists, activeStoreClient?.price_list_id]
    );

    const buildStorefrontProducts = useCallback((sourceProducts, priceList) => {
        return (sourceProducts || []).map((product) => {
            const basePrice = getPriceListAdjustedBasePrice(product, priceList);

            return {
                ...product,
                price: basePrice,
                variants: (product.product_variants || []).map((variant) => ({
                    id: variant.id,
                    name: getCleanVariantLabel(variant.sku || variant.description || 'Opcion'),
                    priceMod: getPriceListAdjustedVariantPrice(product, variant, priceList) - basePrice,
                    stock: product.stock,
                })),
            };
        });
    }, []);

    useEffect(() => {
        setProducts(buildStorefrontProducts(rawProducts, activePriceList));
    }, [rawProducts, activePriceList, buildStorefrontProducts]);

    useEffect(() => {
        setCart((current) => current.map((item) => {
            const rawProduct = rawProducts.find((product) => product.id === item.id);
            if (!rawProduct) return item;

            const rawVariant = item.variantId
                ? (rawProduct.product_variants || []).find((variant) => variant.id === item.variantId) || null
                : null;
            const nextPrice = rawVariant
                ? getPriceListAdjustedVariantPrice(rawProduct, rawVariant, activePriceList)
                : getPriceListAdjustedBasePrice(rawProduct, activePriceList);

            return {
                ...item,
                price: nextPrice,
            };
        }));
    }, [activePriceList, rawProducts]);

    useEffect(() => {
        if (!config?.organization_id) return undefined;

        const queueSilentRefresh = () => {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = setTimeout(() => {
                fetchStoreData({ silent: true });
            }, 250);
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                queueSilentRefresh();
            }
        };

        const handleStorefrontRefresh = (event) => {
            const detail = event?.detail || {};
            if (
                detail.organizationId
                && detail.organizationId !== config.organization_id
                && detail.slug !== slug
            ) {
                return;
            }

            queueSilentRefresh();
        };

        const handleStorage = (event) => {
            if (event.key !== STOREFRONT_REFRESH_STORAGE_KEY || !event.newValue) return;

            try {
                const payload = JSON.parse(event.newValue);
                handleStorefrontRefresh({ detail: payload });
            } catch (error) {
                console.error('No se pudo interpretar el refresh de tienda.', error);
                queueSilentRefresh();
            }
        };

        const channel = supabase
            .channel(`public_store_live_${config.organization_id}_${slug}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'store_config', filter: `organization_id=eq.${config.organization_id}` }, queueSilentRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'products', filter: `organization_id=eq.${config.organization_id}` }, queueSilentRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'product_variants', filter: `organization_id=eq.${config.organization_id}` }, queueSilentRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'product_categories', filter: `organization_id=eq.${config.organization_id}` }, queueSilentRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'payment_methods', filter: `organization_id=eq.${config.organization_id}` }, queueSilentRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'stock_levels', filter: `organization_id=eq.${config.organization_id}` }, queueSilentRefresh)
            .subscribe();

        const intervalId = window.setInterval(() => {
            if (document.visibilityState !== 'hidden') {
                queueSilentRefresh();
            }
        }, 15000);

        window.addEventListener('focus', queueSilentRefresh);
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener(STOREFRONT_REFRESH_EVENT, handleStorefrontRefresh);
        window.addEventListener('storage', handleStorage);

        return () => {
            clearTimeout(refreshTimerRef.current);
            window.clearInterval(intervalId);
            supabase.removeChannel(channel);
            window.removeEventListener('focus', queueSilentRefresh);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener(STOREFRONT_REFRESH_EVENT, handleStorefrontRefresh);
            window.removeEventListener('storage', handleStorage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config?.organization_id, slug]);

    const priceAccessStorageKey = useMemo(
        () => buildStorePriceAccessKey(slug, config?.organization_id),
        [slug, config?.organization_id]
    );

    const persistStorePriceAccess = useCallback((clientRow) => {
        if (!clientRow?.id) return;
        const normalizedCuit = normalizeDocument(clientRow.doc_number);
        if (normalizedCuit.length !== 11) return;
        localStorage.setItem(priceAccessStorageKey, JSON.stringify({
            cuit: normalizedCuit,
            clientId: clientRow.id,
            ts: Date.now(),
        }));
    }, [priceAccessStorageKey]);

    const clearStorePriceAccess = useCallback(() => {
        localStorage.removeItem(priceAccessStorageKey);
        setActiveStoreClient(null);
        setPriceAccessCuit('');
        setPriceAccessError('');
        setCart([]);
        setQtyDrafts({});
        setSelectedProduct(null);
        setIsCartOpen(false);
        setCheckoutOpen(false);
    }, [priceAccessStorageKey]);

    const findClientByCuit = useCallback(async (submittedCuit) => {
        if (!config?.organization_id) return null;
        const normalizedCuit = normalizeDocument(submittedCuit);
        if (normalizedCuit.length !== 11) return null;

        const { data, error } = await supabase
            .from('clients')
            .select('id, full_name, doc_number, doc_type, price_list_id, phone, address, city, is_active')
            .eq('organization_id', config.organization_id)
            .not('doc_number', 'is', null);

        if (error) throw error;

        return (data || []).find((row) => {
            if (row?.is_active === false) return false;
            return normalizeDocument(row.doc_number) === normalizedCuit;
        }) || null;
    }, [config?.organization_id]);

    useEffect(() => {
        if (!config?.organization_id || !config?.theme_config?.requireCuitForPriceView) return undefined;

        const storedValue = localStorage.getItem(priceAccessStorageKey);
        if (!storedValue) return undefined;

        let cancelled = false;

        const restoreAccess = async () => {
            try {
                const parsed = JSON.parse(storedValue);
                const storedCuit = normalizeDocument(parsed?.cuit);
                if (storedCuit.length !== 11) {
                    localStorage.removeItem(priceAccessStorageKey);
                    return;
                }

                setPriceAccessCuit(storedCuit);
                setPriceAccessLoading(true);
                const matchedClient = await findClientByCuit(storedCuit);
                if (cancelled) return;

                if (!matchedClient) {
                    localStorage.removeItem(priceAccessStorageKey);
                    setActiveStoreClient(null);
                    setPriceAccessError('No encontramos un cliente activo asociado a ese CUIT.');
                    return;
                }

                setActiveStoreClient(matchedClient);
                setPriceAccessError('');
            } catch (error) {
                console.error('No se pudo restaurar el acceso por CUIT.', error);
                localStorage.removeItem(priceAccessStorageKey);
            } finally {
                if (!cancelled) setPriceAccessLoading(false);
            }
        };

        restoreAccess();

        return () => {
            cancelled = true;
        };
    }, [config?.organization_id, config?.theme_config?.requireCuitForPriceView, findClientByCuit, priceAccessStorageKey]);

    const fetchStoreData = async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);

            // 1. Configuración
            const { data: storeData, error: storeError } = await supabase
                .from('store_config')
                .select('*')
                .ilike('slug', slug)
                .maybeSingle();

            if (storeError) throw storeError;
            if (!storeData) throw new Error("Tienda no encontrada");

            setConfig(storeData);

            // Determinar depósito para stock
            let defaultSiteId = storeData.default_fulfillment_site_id;
            if (!defaultSiteId) {
                const { data: siteData } = await supabase.from('sitios')
                    .select('id')
                    .eq('organization_id', storeData.organization_id)
                    .eq('is_active', true)
                    .limit(1)
                    .maybeSingle();
                if (siteData) defaultSiteId = siteData.id;
            }
            setSiteId(defaultSiteId);

            const { data: priceListsData, error: priceListsError } = await supabase
                .from('price_lists')
                .select('*')
                .eq('organization_id', storeData.organization_id)
                .eq('is_active', true);

            if (priceListsError) throw priceListsError;
            setPriceLists(priceListsData || []);

            // Obtener Métodos de Pago Habilitados
            const enabledPMIds = storeData.theme_config?.enabled_payment_methods || [];
            
            if (enabledPMIds.length > 0) {
                const paymentMethodLabels = storeData.theme_config?.payment_method_labels || {};
                const { data: pmData, error: pmError } = await supabase
                    .from('payment_methods')
                    .select('*')
                    .in('id', enabledPMIds)
                    .eq('is_active', true);
                    
                if (!pmError && pmData) {
                    const sortedPaymentMethods = enabledPMIds
                        .map((id) => pmData.find((pm) => pm.id === id))
                        .filter(Boolean)
                        .map((pm) => ({
                            ...pm,
                            display_name: paymentMethodLabels[pm.id] || pm.name,
                        }));

                    setEnabledPaymentMethods(sortedPaymentMethods);
                }
            } else {
                // Backward compatibility: If no methods selected in config, keep it empty. 
                // CheckoutModal will handle the lack of methods by showing default ones.
                setEnabledPaymentMethods([]);
            }

            // 2. Productos Activos + Variantes + Categoría
            await supabase
                .from('products')
                .select(`
            *, 
            product_variants (*), 
            product_categories (name)
        `)
                .eq('organization_id', storeData.organization_id)
                .not('is_active', 'is', false)
                .not('active', 'is', false)
                .is('archived_at', null)
                .then(async ({ data: prodData, error: prodError }) => {
                    if (prodError) throw prodError;

                    // 3. Niveles de Stock
                    let stockMap = {};
                    if (defaultSiteId) {
                        const { data: stockData } = await supabase
                            .from('stock_levels')
                            .select('product_id, quantity')
                            .eq('site_id', defaultSiteId);

                        if (stockData) {
                            stockData.forEach(s => { stockMap[s.product_id] = s.quantity; });
                        }
                    }

                    // 4. Procesar productos para el frontend
                    const processedProducts = (prodData || []).map(p => {
                        let totalStock = 0;
                         
                        // Unified stock logic: always use 'stock_levels' regardless of variants
                        totalStock = stockMap[p.id] || 0;

                        return {
                            ...p,
                            name: sanitizeProductDisplayName(p.name || 'Producto'),
                            stock: totalStock,
                            categoryName: p.product_categories?.name || 'General'
                        };
                    });
                    setRawProducts(processedProducts);
                });

        } catch (err) {
            console.error(err);
            if (!silent) toast.error("No se pudo cargar la tienda");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // --- LÓGICA DE CARRITO ---
    const addToCart = (product, qty, note, variant) => {
        const finalPrice = variant ? (product.price + variant.priceMod) : product.price;
        const cartItemId = variant ? `${product.id}-${variant.id}` : `${product.id}`;
        const variantName = getCleanVariantLabel(variant?.name);
        const itemStock = variant ? variant.stock : product.stock;
        let blockedByStock = false;

        setCart(prev => {
            const existing = prev.find(item => item.cartItemId === cartItemId);
            if (existing) {
                const nextQty = !config?.theme_config?.allow_checkout_without_stock && itemStock !== undefined
                    ? Math.min(item.qty + qty, itemStock)
                    : item.qty + qty;

                if (!config?.theme_config?.allow_checkout_without_stock && itemStock !== undefined && nextQty <= item.qty) {
                    blockedByStock = true;
                    return prev;
                }

                return prev.map(item => item.cartItemId === cartItemId ? { ...item, qty: nextQty, note } : item);
            }

            if (!config?.theme_config?.allow_checkout_without_stock && itemStock !== undefined && qty > itemStock) {
                blockedByStock = true;
                return prev;
            }

            return [...prev, {
                ...product,
                name: sanitizeProductDisplayName(product.name || 'Producto'),
                cartItemId,
                price: finalPrice,
                qty,
                note,
                variantName,
                variantId: variant?.id,
                stock: itemStock
            }];
        });

        if (blockedByStock) {
            toast.error("Sin stock disponible");
            return;
        }

        toast.success("Agregado al carrito");
        setSelectedProduct(null);
    };

    const removeFromCart = (cartItemId) => {
        setCart(prev => prev.filter(item => item.cartItemId !== cartItemId));
        setQtyDrafts(prev => {
            const next = { ...prev };
            delete next[cartItemId];
            return next;
        });
    };

    const updateQty = (cartItemId, delta) => {
        let maxStockHit = false;
        let nextQtyForDraft = null;
        setCart(prev => {
            const result = prev.map(item => {
                if (item.cartItemId === cartItemId) {
                    const newQty = item.qty + delta;
                    if (!config?.theme_config?.allow_checkout_without_stock && delta > 0 && item.stock !== undefined && newQty > item.stock) {
                        maxStockHit = true;
                        nextQtyForDraft = item.qty;
                        return item;
                    }
                    nextQtyForDraft = newQty;
                    return { ...item, qty: newQty };
                }
                return item;
            }).filter(item => item.qty > 0);
            return result;
        });
        if (nextQtyForDraft && nextQtyForDraft > 0) {
            setQtyDrafts(prev => ({
                ...prev,
                [cartItemId]: String(nextQtyForDraft),
            }));
        }
        if (maxStockHit) setTimeout(() => toast.error("Sin stock disponible"), 100);
    };

    const handleQtyDraftChange = (cartItemId, value) => {
        const sanitized = String(value || '').replace(/[^\d]/g, '');
        setQtyDrafts(prev => ({
            ...prev,
            [cartItemId]: sanitized,
        }));
    };

    const commitQtyDraft = (cartItemId) => {
        const rawValue = String(qtyDrafts[cartItemId] || '').trim();
        if (!rawValue) {
            removeFromCart(cartItemId);
            return;
        }

        const requestedQty = Number.parseInt(rawValue, 10);
        if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
            removeFromCart(cartItemId);
            return;
        }

        let blockedByStock = false;
        let finalQty = requestedQty;

        setCart(prev =>
            prev.map(item => {
                if (item.cartItemId !== cartItemId) return item;

                if (!config?.theme_config?.allow_checkout_without_stock && item.stock !== undefined) {
                    const limitedQty = Math.min(requestedQty, Math.max(Number(item.stock) || 0, 0));
                    if (limitedQty !== requestedQty) blockedByStock = true;
                    finalQty = limitedQty;
                }

                return { ...item, qty: finalQty };
            }).filter(item => item.qty > 0)
        );

        if (finalQty > 0) {
            setQtyDrafts(prev => ({
                ...prev,
                [cartItemId]: String(finalQty),
            }));
        } else {
            removeFromCart(cartItemId);
            return;
        }

        if (blockedByStock) {
            toast.error("Sin stock disponible");
        }
    };

    useEffect(() => {
        setQtyDrafts(prev => {
            const next = {};
            let changed = false;

            cart.forEach(item => {
                next[item.cartItemId] = prev[item.cartItemId] ?? String(item.qty);
                if (next[item.cartItemId] !== prev[item.cartItemId]) changed = true;
            });

            if (Object.keys(prev).length !== cart.length) changed = true;

            return changed ? next : prev;
        });
    }, [cart]);

    useEffect(() => {
        if (!config?.theme_config?.requireCuitForPriceView || activeStoreClient) return;
        setSelectedProduct(null);
        setIsCartOpen(false);
        setCheckoutOpen(false);
    }, [config?.theme_config?.requireCuitForPriceView, activeStoreClient]);

    const cartTotal = cart.reduce((acc, item) => acc + (item.price * item.qty), 0);

    // --- FILTROS ---
    const brandFilterEnabled = Boolean(config?.theme_config?.enable_brand_filter);
    const sortingEnabled = Boolean(config?.theme_config?.enable_sorting);
    const categories = ['Todos', ...new Set(products.map(p => p.categoryName))];

    const brandOptions = buildBrandOptions(
        products.filter(p => normalizeBrandLabel(p.brand, '') !== ''),
        (p) => p.brand,
    );
    const effectiveBrand = brandOptions.some(b => b.value === selectedBrand) ? selectedBrand : 'todas';

    const SORT_OPTIONS = [
        { value: 'relevancia', label: 'Destacados' },
        { value: 'nombre_asc', label: 'Nombre: A \u2192 Z' },
        { value: 'nombre_desc', label: 'Nombre: Z \u2192 A' },
        { value: 'precio_asc', label: 'Precio: menor a mayor' },
        { value: 'precio_desc', label: 'Precio: mayor a menor' },
        { value: 'marca_asc', label: 'Marca: A \u2192 Z' },
        { value: 'marca_desc', label: 'Marca: Z \u2192 A' },
    ];

    const filteredProducts = (() => {
        const result = products.filter(p => {
            const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'Todos' || p.categoryName === selectedCategory;
            const matchesBrand = !brandFilterEnabled || effectiveBrand === 'todas'
                || normalizeBrandKey(p.brand, '') === effectiveBrand;
            return matchesSearch && matchesCategory && matchesBrand;
        });
        if (sortingEnabled && sortBy !== 'relevancia') {
            const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });
            const byName = (a, b) => collator.compare(a.name || '', b.name || '');
            const byPrice = (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0);
            const byBrand = (a, b) => collator.compare(normalizeBrandLabel(a.brand, ''), normalizeBrandLabel(b.brand, ''));
            const comparators = {
                nombre_asc: byName,
                nombre_desc: (a, b) => -byName(a, b),
                precio_asc: byPrice,
                precio_desc: (a, b) => -byPrice(a, b),
                marca_asc: (a, b) => byBrand(a, b) || byName(a, b),
                marca_desc: (a, b) => -byBrand(a, b) || byName(a, b),
            };
            const cmp = comparators[sortBy];
            if (cmp) result.sort(cmp);
        }
        return result;
    })();

    const handleSearchOrders = async (e) => {
        e.preventDefault();
        if (!rastreoPhone || rastreoPhone.length < 6) {
            toast.error('Ingresa un número de celular válido');
            return;
        }

        setRastreoLoading(true);
        setRastreoSearched(true);
        try {
            const { data, error } = await supabase
                .from('store_orders')
                .select(`
                    *,
                    sale:sales!store_orders_sale_id_fkey (total)
                `)
                .eq('organization_id', config.organization_id)
                .or(`customer_phone.ilike.%${rastreoPhone}%,customer_snapshot->>phone.ilike.%${rastreoPhone}%`)
                .order('created_at', { ascending: false })
                .limit(10);
            
            if (error) throw error;
            setRastreoOrders((data || []).filter(isStoreOrderFromWeb));
        } catch (error) {
            console.error(error);
            toast.error('Error al buscar pedidos');
        } finally {
            setRastreoLoading(false);
        }
    };

    const handlePriceAccessSubmit = async (e) => {
        e.preventDefault();
        const normalizedCuit = normalizeDocument(priceAccessCuit);

        if (normalizedCuit.length !== 11) {
            setPriceAccessError('Ingresa un CUIT valido de 11 digitos.');
            toast.error('Ingresa un CUIT valido');
            return;
        }

        setPriceAccessLoading(true);
        setPriceAccessError('');
        try {
            const matchedClient = await findClientByCuit(normalizedCuit);
            if (!matchedClient) {
                localStorage.removeItem(priceAccessStorageKey);
                setActiveStoreClient(null);
                setPriceAccessError('No encontramos un cliente activo asociado a ese CUIT.');
                toast.error('No encontramos un cliente con ese CUIT');
                return;
            }

            setActiveStoreClient(matchedClient);
            setPriceAccessCuit(normalizedCuit);
            persistStorePriceAccess(matchedClient);
            setCart([]);
            setQtyDrafts({});
            setSelectedProduct(null);
            toast.success(`Precios habilitados para ${matchedClient.full_name || 'cliente'}`);
        } catch (error) {
            console.error(error);
            setPriceAccessError('No pudimos validar el CUIT en este momento.');
            toast.error('No se pudo validar el CUIT');
        } finally {
            setPriceAccessLoading(false);
        }
    };

    // --- RENDER ---
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--surface-2)]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
    );

    if (!config) return <div className="p-10 text-center text-[var(--text-muted)]">Tienda no encontrada.</div>;

    const isGastronomy = config.store_type === 'gastronomy';
    const storeStatus = checkStoreStatus(config.open_hours, config.always_open);
    const theme = config.theme_config || {};
    const primaryColor = theme.primaryColor || (isGastronomy ? '#ea580c' : '#0f172a');
    const storeBackgroundColor = theme.storeBackgroundColor || withAlpha(primaryColor, '08') || '#f9fafb';
    const cardBg = theme.cardColors?.bg || '#ffffff';
    const cardBorder = theme.cardColors?.border || '#e5e7eb';
    const cardText = theme.cardColors?.text || '#111827';
    const modalBg = theme.modalColors?.bg || '#ffffff';
    const modalBorder = theme.modalColors?.border || '#e5e7eb';
    const modalText = theme.modalColors?.text || '#111827';
    const welcomePopup = theme.welcomePopup || {};
    const softPrimaryBg = withAlpha(primaryColor, '14');
    const softPrimaryBorder = withAlpha(primaryColor, '33');
    const softPrimaryText = withAlpha(primaryColor, 'EE');
    const allowCheckoutWithoutStock = Boolean(config.theme_config?.allow_checkout_without_stock);
    // Aviso de ultimas unidades: informativo, no bloquea el checkout.
    const lowStockAlertConfig = getLowStockAlertConfig(config);
    const lowStockCartItems = getLowStockCartItems(cart, lowStockAlertConfig);
    const requiresCuitForPriceView = Boolean(theme.requireCuitForPriceView);
    const showStorePrices = theme.showPrices !== false && (!requiresCuitForPriceView || Boolean(activeStoreClient));
    const isPriceAccessLocked = requiresCuitForPriceView && !activeStoreClient;

    return (
        <div className="min-h-screen font-sans pb-20" style={{ backgroundColor: storeBackgroundColor }}>

            {welcomePopupOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                    <button
                        type="button"
                        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
                        onClick={() => setWelcomePopupOpen(false)}
                        aria-label="Cerrar bienvenida"
                    />
                    <div
                        className="relative w-full max-w-lg rounded-[2rem] border shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
                        style={{ backgroundColor: modalBg, borderColor: modalBorder, color: modalText }}
                    >
                        <div
                            className="px-6 py-5 border-b"
                            style={{ borderColor: modalBorder, backgroundColor: withAlpha(primaryColor, '12') }}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: primaryColor }}>
                                        Mensaje de bienvenida
                                    </p>
                                    <h2 className="mt-2 text-2xl font-black tracking-tight">
                                        {welcomePopup.title || 'Bienvenidos'}
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setWelcomePopupOpen(false)}
                                    className="w-10 h-10 rounded-md border flex items-center justify-center transition-colors hover:bg-[var(--surface)]/60"
                                    style={{ borderColor: modalBorder }}
                                    aria-label="Cerrar popout"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        <div className="px-6 py-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div
                                    className="w-12 h-12 rounded-md flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: softPrimaryBg, color: primaryColor }}
                                >
                                    <ShoppingBag size={22} />
                                </div>
                                <p className="text-sm leading-relaxed opacity-90">
                                    {welcomePopup.message || 'Gracias por visitar nuestra tienda. Mira nuestras novedades y promociones antes de hacer tu pedido.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setWelcomePopupOpen(false)}
                                className="w-full mt-2 px-4 py-3 rounded-md text-sm font-black uppercase tracking-widest text-[var(--color-accent-dark)] shadow-lg hover:opacity-95 transition-opacity"
                                style={{ backgroundColor: primaryColor }}
                            >
                                {welcomePopup.buttonText || 'Ver tienda'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BANNER & CARRUSEL / HEADER */}
            <div className="relative h-48 md:h-64 w-full bg-slate-900 overflow-hidden shadow-md">
                {carouselSlides.length > 0 ? (
                    <>
                        <div className="absolute inset-0 transition-opacity duration-1000">
                            <img
                                src={carouselSlides[Math.min(currentSlide, carouselSlides.length - 1)]}
                                alt="Carrusel"
                                className="w-full h-full object-cover opacity-60 animate-in fade-in"
                                onError={(e) => {
                                    const failed = e.currentTarget.getAttribute('src');
                                    if (failed) setBrokenSlides((prev) => (prev.includes(failed) ? prev : [...prev, failed]));
                                }}
                            />
                        </div>
                        {carouselSlides.length > 1 && (
                            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
                                {carouselSlides.map((_, idx) => (
                                    <button 
                                        key={idx} 
                                        onClick={() => setCurrentSlide(idx)}
                                        className={`w-2 h-2 rounded-full transition-all ${currentSlide === idx ? 'w-6 bg-[var(--surface)]' : 'bg-[var(--surface)]/50 hover:bg-[var(--surface)]/80'}`}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : config.banner_url ? (
                    <img src={resolveStoreImageUrl(config.banner_url)} alt="Portada" className="w-full h-full object-cover opacity-60" />
                ) : (
                    <div className="w-full h-full opacity-30" style={{ backgroundColor: config.theme_config?.primaryColor || (isGastronomy ? '#ea580c' : '#2563eb') }}></div>
                )}
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex items-end">
                    <div className="max-w-4xl mx-auto w-full px-4 pb-6 lg:pb-8 text-white flex justify-between items-end">
                        <div className="flex items-end gap-3 min-w-0">
                            {config.logo_url && (
                                <img
                                    src={resolveStoreImageUrl(config.logo_url)}
                                    alt={config.store_title || 'Logo'}
                                    className="h-14 lg:h-20 w-auto max-w-[35vw] object-contain drop-shadow-lg shrink-0"
                                    decoding="async"
                                />
                            )}
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    {isGastronomy ? <ChefHat size={20} style={{ color: primaryColor }} /> : <Store size={20} style={{ color: primaryColor }} />}
                                    <span className="text-xs font-bold uppercase tracking-wider opacity-80">
                                        {isGastronomy ? 'Delivery Rápido' : 'Tienda Online'}
                                    </span>
                                </div>
                                <h1 className="text-3xl lg:text-5xl font-black leading-none tracking-tight truncate">{config.store_title}</h1>
                            </div>
                        </div>
                        {/* Status Badge */}
                        <div className={`px-4 py-1.5 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-lg backdrop-blur-md ${storeStatus.isOpen ? 'bg-[var(--color-accent-dark)]/90 text-white' : 'bg-red-500/90 text-white'}`}>
                            <Clock size={14} /> <span className="hidden md:inline">{storeStatus.message}</span><span className="md:hidden">{storeStatus.isOpen ? 'Abierto' : 'Cerrado'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABS DE NAVEGACIÓN PRINCIPAL */}
            <div className="border-b border-gray-100 sticky top-16 md:top-[70px] z-30 shadow-sm" style={{ backgroundColor: cardBg }}>
                <div className="max-w-4xl mx-auto px-4 flex overflow-x-auto no-scrollbar justify-center">
                    <button 
                        onClick={() => setActiveTab('productos')}
                        className={`flex items-center gap-2 px-6 py-4 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${activeTab === 'productos' ? '' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-default)]'}`}
                        style={activeTab === 'productos' ? { borderColor: primaryColor, color: primaryColor } : undefined}
                    >
                        <Package size={16} /> Productos
                    </button>
                    {config.theme_config?.about?.enabled && (
                    <button 
                        onClick={() => setActiveTab('nosotros')}
                        className={`flex items-center gap-2 px-6 py-4 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${activeTab === 'nosotros' ? '' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-default)]'}`}
                        style={activeTab === 'nosotros' ? { borderColor: primaryColor, color: primaryColor } : undefined}
                    >
                        <Store size={16} /> Nuestra Historia
                    </button>
                    )}
                    <button 
                        onClick={() => setActiveTab('rastreo')}
                        className={`flex items-center gap-2 px-6 py-4 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${activeTab === 'rastreo' ? '' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-default)]'}`}
                        style={activeTab === 'rastreo' ? { borderColor: primaryColor, color: primaryColor } : undefined}
                    >
                        <Truck size={16} /> Seguir Pedido
                    </button>
                    {config.theme_config?.contactSection?.enabled !== false && (
                    <button 
                        onClick={() => setActiveTab('contacto')}
                        className={`flex items-center gap-2 px-6 py-4 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${activeTab === 'contacto' ? '' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-default)]'}`}
                        style={activeTab === 'contacto' ? { borderColor: primaryColor, color: primaryColor } : undefined}
                    >
                        <Phone size={16} /> Contacto
                    </button>
                    )}
                </div>
            </div>

            {/* BARRA DE BÚSQUEDA Y CARRITO (Solo visible en Productos) */}
            {activeTab === 'productos' && !isPriceAccessLocked && (
                <div className="sticky top-[110px] md:top-[120px] z-30 backdrop-blur-sm border-b border-gray-100 py-3" style={{ backgroundColor: `${storeBackgroundColor}F2` }}>
                    <div className="max-w-4xl mx-auto px-4 flex flex-col md:flex-row items-center gap-3">
                        
                        <div className="flex w-full md:w-auto flex-1 flex-wrap gap-2">
                            {/* Desplegable de Categorías */}
                            <div className="relative shrink-0">
                                <button
                                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                                    className="h-full px-4 py-2 bg-[var(--surface)] border border-[color:var(--border)] rounded-md text-sm font-bold text-[var(--text-default)] flex items-center gap-2 focus:ring-2 focus:ring-slate-900 outline-none hover:bg-[var(--surface-2)] transition-colors"
                                >
                                    <AlignLeft size={16} className="text-[var(--text-muted)]"/>
                                    <span className="max-w-[100px] truncate">{selectedCategory}</span>
                                    <ChevronDown size={14} className="text-[var(--text-muted)]" />
                                </button>
                                
                                {isCategoryOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsCategoryOpen(false)} />
                                        <div className="absolute top-12 left-0 w-56 bg-[var(--surface)] border border-gray-100 shadow-xl rounded-xl z-50 py-2 animate-in fade-in slide-in-from-top-2">
                                            <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] border-b border-gray-100 mb-2">Filtrar por</div>
                                            <div className="max-h-60 overflow-y-auto no-scrollbar px-2 space-y-1">
                                                {categories.map(cat => (
                                                    <button
                                                        key={cat}
                                                        onClick={() => { setSelectedCategory(cat); setIsCategoryOpen(false); }}
                                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat ? 'text-white' : 'hover:bg-[var(--surface-2)] text-[var(--text-muted)]'}`}
                                                        style={selectedCategory === cat ? { backgroundColor: primaryColor } : undefined}
                                                    >
                                                        {cat}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Desplegable de Marcas */}
                            {brandFilterEnabled && brandOptions.length > 0 && (
                                <div className="relative shrink-0">
                                    <button
                                        onClick={() => setIsBrandOpen(!isBrandOpen)}
                                        className="h-full px-4 py-2 bg-[var(--surface)] border border-[color:var(--border)] rounded-md text-sm font-bold text-[var(--text-default)] flex items-center gap-2 focus:ring-2 focus:ring-slate-900 outline-none hover:bg-[var(--surface-2)] transition-colors"
                                    >
                                        <Tag size={16} className="text-[var(--text-muted)]"/>
                                        <span className="max-w-[100px] truncate">
                                            {effectiveBrand === 'todas'
                                                ? 'Marca'
                                                : (brandOptions.find(b => b.value === effectiveBrand)?.label || 'Marca')}
                                        </span>
                                        <ChevronDown size={14} className="text-[var(--text-muted)]" />
                                    </button>

                                    {isBrandOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setIsBrandOpen(false)} />
                                            <div className="absolute top-12 left-0 w-56 bg-[var(--surface)] border border-gray-100 shadow-xl rounded-xl z-50 py-2 animate-in fade-in slide-in-from-top-2">
                                                <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] border-b border-gray-100 mb-2">Filtrar por marca</div>
                                                <div className="max-h-60 overflow-y-auto no-scrollbar px-2 space-y-1">
                                                    {[{ value: 'todas', label: 'Todas las marcas' }, ...brandOptions].map(brand => (
                                                        <button
                                                            key={brand.value}
                                                            onClick={() => { setSelectedBrand(brand.value); setIsBrandOpen(false); }}
                                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${effectiveBrand === brand.value ? 'text-white' : 'hover:bg-[var(--surface-2)] text-[var(--text-muted)]'}`}
                                                            style={effectiveBrand === brand.value ? { backgroundColor: primaryColor } : undefined}
                                                        >
                                                            {brand.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Buscador */}
                            <div className="flex-1 relative min-w-[140px]">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                <input
                                    type="text"
                                    placeholder="Buscar producto..."
                                    className="w-full pl-9 pr-4 py-2 bg-[var(--surface)] border border-[color:var(--border)] rounded-md text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all shadow-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Ordenar */}
                            {sortingEnabled && (
                                <div className="relative shrink-0">
                                    <ArrowDownUp size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
                                    <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        aria-label="Ordenar productos"
                                        className="h-full appearance-none pl-9 pr-7 py-2 bg-[var(--surface)] border border-[color:var(--border)] rounded-md text-sm font-bold text-[var(--text-default)] focus:ring-2 focus:ring-slate-900 outline-none hover:bg-[var(--surface-2)] transition-colors cursor-pointer"
                                    >
                                        {SORT_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Botón Carrito */}
                        <button
                            onClick={() => setIsCartOpen(true)}
                            style={cart.length > 0 ? { backgroundColor: config.theme_config?.primaryColor || (isGastronomy ? '#f97316' : '#0f172a') } : {}}
                            className={`w-full md:w-auto relative flex justify-center items-center gap-2 transition-all font-black shrink-0 ${
                                cart.length > 0
                                    ? 'px-6 py-2 md:py-3 rounded-xl shadow-xl shadow-current/20 text-white hover:-translate-y-1 hover:shadow-2xl'
                                    : 'px-4 py-2 rounded-xl bg-[var(--surface)] border border-[color:var(--border)] text-[var(--text-muted)] hover:bg-[var(--surface-2)] text-sm'
                            }`}
                        >
                            <ShoppingCart size={cart.length > 0 ? 20 : 18} className={cart.length > 0 ? "animate-bounce" : ""} />
                            {cart.length > 0 ? (
                                <span className="text-sm font-black tracking-wide">
                                    {cart.reduce((acc, item) => acc + item.qty, 0)} Items
                                </span>
                            ) : <span>Mi Pedido</span>}
                            {cart.length > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 animate-pulse border-2 border-white shadow-sm"></span>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* CONTENIDO PRINCIPAL DEPENDIENDO DEL TAB */}
            {activeTab === 'nosotros' && config.theme_config?.about?.enabled && (
                <div className="max-w-4xl mx-auto px-4 py-12 animate-in fade-in slide-in-from-bottom-4">
                    <div className="bg-[var(--surface)] rounded-[2rem] p-8 md:p-12 flex flex-col md:flex-row gap-10 items-center shadow-sm border" style={{ borderColor: modalBorder }}>
                        {config.theme_config.about.image && (
                            <div className="w-48 h-48 md:w-64 md:h-64 shrink-0 rounded-lg overflow-hidden shadow-xl shadow-slate-200/50">
                                <img src={resolveStoreImageUrl(config.theme_config.about.image)} alt="Nosotros" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div>
                            <div className="w-12 h-1 rounded-full mb-6" style={{ backgroundColor: primaryColor }}></div>
                            <h2 className="text-3xl md:text-4xl font-black text-[var(--text-default)] mb-6 tracking-tight">{config.theme_config.about.title || 'Nuestra Historia'}</h2>
                            <p className="text-[var(--text-muted)] text-sm md:text-base leading-relaxed whitespace-pre-line">{config.theme_config.about.text}</p>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'contacto' && (
                <div className="max-w-4xl mx-auto px-4 py-12 animate-in fade-in slide-in-from-bottom-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {config.theme_config?.contactSection?.enabled !== false && config.theme_config?.locations?.length > 0 && (
                            <div className="bg-[var(--surface)] p-8 rounded-[2rem] shadow-sm border" style={{ borderColor: modalBorder }}>
                                <div className="w-12 h-12 rounded-md flex items-center justify-center mb-6" style={{ backgroundColor: softPrimaryBg, color: primaryColor }}>
                                    <MapPin size={24} />
                                </div>
                                <h3 className="text-xl font-black text-[var(--text-default)] mb-6">Nuestras Ubicaciones</h3>
                                <ul className="space-y-4">
                                    {config.theme_config.locations.map((loc, idx) => (
                                        <li key={idx} className="flex gap-3 text-[var(--text-muted)] bg-[var(--surface-2)] p-4 rounded-md border" style={{ borderColor: modalBorder }}>
                                            <Navigation size={18} className="shrink-0 mt-0.5" style={{ color: primaryColor }} />
                                            <span className="font-medium text-sm leading-snug">{loc}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="bg-[var(--surface)] p-8 rounded-[2rem] shadow-sm border" style={{ borderColor: modalBorder }}>
                            <div className="w-12 h-12 rounded-md flex items-center justify-center mb-6" style={{ backgroundColor: softPrimaryBg, color: primaryColor }}>
                                <Clock size={24} />
                            </div>
                            <h3 className="text-xl font-black text-[var(--text-default)] mb-6">Horario de Atención</h3>
                            <div className="text-[var(--text-muted)] text-sm space-y-2 bg-[var(--surface-2)] p-5 rounded-md border" style={{ borderColor: modalBorder }}>
                                {config.always_open ? (
                                    <div className="flex items-center gap-2 font-black text-[var(--color-accent-dark)]">
                                        <div className="w-2 h-2 rounded-full bg-[var(--color-accent-dark)] animate-pulse"></div>
                                        ¡Abiertos 24 horas, todos los días!
                                    </div>
                                ) : (
                                    <>
                                        <p className="font-medium">Consulta si estamos abiertos antes de realizar tu pedido.</p>
                                        <div className={`mt-4 inline-flex px-4 py-2 rounded-md text-xs font-black uppercase tracking-widest gap-2 shadow-sm border ${storeStatus.isOpen ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] border-[color:var(--color-accent)]' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                            <Clock size={14} /> {storeStatus.message}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'rastreo' && (
                <div className="max-w-4xl mx-auto px-4 py-12 animate-in fade-in slide-in-from-bottom-4">
                    <div className="bg-[var(--surface)] rounded-[2rem] p-8 md:p-12 shadow-sm border" style={{ borderColor: modalBorder }}>
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: softPrimaryBg, color: primaryColor }}>
                                <Truck size={32} />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-black text-[var(--text-default)] mb-2">Seguí tu pedido</h2>
                            <p className="text-[var(--text-muted)] text-sm">Ingresá tu número de celular para ver el estado de tus compras.</p>
                        </div>

                        <form onSubmit={handleSearchOrders} className="max-w-md mx-auto mb-10">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                    <input
                                        type="tel"
                                        placeholder="Tu número de celular..."
                                        className="w-full pl-11 pr-4 py-3 bg-[var(--surface)] border border-[color:var(--border)] rounded-md text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none transition-all"
                                        value={rastreoPhone}
                                        onChange={(e) => setRastreoPhone(e.target.value)}
                                        disabled={rastreoLoading}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={rastreoLoading}
                                    style={{ backgroundColor: config.theme_config?.primaryColor || (isGastronomy ? '#ea580c' : '#0f172a') }}
                                    className="px-6 py-3 text-white rounded-md font-bold text-sm hover:opacity-90 transition-colors shadow-lg disabled:opacity-50 flex items-center gap-2"
                                >
                                    {rastreoLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Search size={18} />}
                                    Buscar
                                </button>
                            </div>
                        </form>

                        {rastreoSearched && (
                            <div className="max-w-2xl mx-auto space-y-4">
                                {rastreoLoading ? (
                                    <div className="text-center py-10">
                                        <div className="w-8 h-8 border-4 border-[color:var(--border)] rounded-full animate-spin mx-auto mb-4" style={{ borderTopColor: primaryColor }}></div>
                                        <p className="text-[var(--text-muted)] font-bold uppercase tracking-widest text-xs">Buscando pedidos...</p>
                                    </div>
                                ) : rastreoOrders.length === 0 ? (
                                    <div className="text-center py-12 bg-[var(--surface-2)] rounded-md border border-slate-100">
                                        <Package size={48} className="mx-auto mb-4 text-slate-300" />
                                        <p className="font-bold text-[var(--text-muted)]">No encontramos pedidos</p>
                                        <p className="text-sm text-[var(--text-muted)] mt-1">Verificá que el número ingresado sea el mismo que usaste al comprar.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <h3 className="font-black text-[var(--color-accent-dark)] uppercase tracking-widest text-xs mb-4 px-2">Historial de Pedidos</h3>
                                        {rastreoOrders.map(order => (
                                            <div 
                                                key={order.id} 
                                                onClick={() => navigate(`/rastreo/${order.tracking_code}`)}
                                                className="bg-[var(--surface)] border border-[color:var(--border)] rounded-md p-5 shadow-sm hover:shadow-md transition-all cursor-pointer"
                                            >
                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-black text-[var(--text-default)]">#{order.tracking_code}</span>
                                                            <span className="text-xs font-bold text-[var(--text-muted)] line-clamp-1">{new Date(order.created_at).toLocaleString('es-AR')}</span>
                                                        </div>
                                                        <div className="text-sm text-[var(--text-muted)] font-medium">
                                                            {order.delivery_type === 'envio' ? (
                                                                <span className="flex items-center gap-1.5"><MapPin size={14}/> Envío a domicilio</span>
                                                            ) : (
                                                                <span className="flex items-center gap-1.5"><Store size={14}/> Retiro por local</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex items-center justify-between md:justify-end gap-4 md:w-auto w-full">
                                                        <div className="text-left md:text-right">
                                                            <div className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-widest mb-0.5">Total</div>
                                                            <div className="font-black text-[var(--text-default)]">
                                                                {showStorePrices ? formatCurrency(order.sale?.total || 0) : 'Precio oculto'}
                                                            </div>
                                                        </div>
                                                        <div className={`px-4 py-2 rounded-md text-xs font-black uppercase tracking-widest border ${getStatusColor(order.status)} shrink-0`}>
                                                            {STATUS_LABELS[order.status] || order.status}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* GRILLA / LISTA DE PRODUCTOS */}
            {activeTab === 'productos' && (
            <main className="max-w-4xl mx-auto px-4 py-6 animate-in fade-in">

                {/* Aviso si está cerrado */}
                {!storeStatus.isOpen && !config.always_open && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-700 text-sm font-bold">
                        <Clock size={20} />
                        El comercio se encuentra cerrado en este momento.
                    </div>
                )}

                {/* Texto Promocional */}
                {config.financing_text && (
                    <div className="mb-6 p-3 rounded-xl flex items-center gap-3 text-sm font-medium border" style={{ backgroundColor: softPrimaryBg, borderColor: softPrimaryBorder, color: softPrimaryText }}>
                        <CreditCard size={18} />
                        {config.financing_text}
                    </div>
                )}

                {requiresCuitForPriceView && activeStoreClient && (
                    <div className="mb-6 rounded-[1.75rem] border bg-[var(--surface)] p-4 md:p-5 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3" style={{ borderColor: modalBorder }}>
                        <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-[0.24em]" style={{ color: primaryColor }}>
                                Acceso habilitado por CUIT
                            </p>
                            <p className="mt-2 text-sm font-black text-[var(--color-accent-dark)] truncate">
                                {activeStoreClient.full_name || 'Cliente identificado'}
                            </p>
                            <p className="text-[11px] text-[var(--text-muted)]">
                                CUIT {normalizeDocument(activeStoreClient.doc_number)}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={clearStorePriceAccess}
                            className="px-4 py-2.5 rounded-md border border-[color:var(--border)] text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)] hover:bg-[var(--surface-2)] transition-colors"
                        >
                            Cambiar CUIT
                        </button>
                    </div>
                )}

                {isPriceAccessLocked ? (
                    <div className="py-10">
                        <div className="max-w-xl mx-auto rounded-[2rem] border bg-[var(--surface)] p-6 md:p-8 shadow-sm" style={{ borderColor: modalBorder }}>
                            <div className="w-14 h-14 rounded-md flex items-center justify-center mb-5" style={{ backgroundColor: softPrimaryBg, color: primaryColor }}>
                                <Store size={28} />
                            </div>
                            <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: primaryColor }}>
                                Acceso a lista de precios
                            </p>
                            <h2 className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-[var(--text-default)]">
                                Ingresa tu CUIT para ver el catalogo con precios
                            </h2>
                            <p className="mt-3 text-sm leading-relaxed text-[var(--text-muted)]">
                                Esta tienda requiere validar un CUIT asociado a un cliente activo antes de mostrar los importes y habilitar la compra.
                            </p>

                            <form onSubmit={handlePriceAccessSubmit} className="mt-6 space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                                        CUIT
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="Ej: 30712345678"
                                        value={priceAccessCuit}
                                        onChange={(event) => {
                                            setPriceAccessCuit(normalizeDocument(event.target.value).slice(0, 11));
                                            if (priceAccessError) setPriceAccessError('');
                                        }}
                                        className="w-full rounded-md border border-[color:var(--border)] bg-[var(--surface)] px-4 py-3 text-base font-bold tracking-wide text-[var(--text-default)] outline-none transition-all focus:border-slate-400 focus:bg-[var(--surface)]"
                                        disabled={priceAccessLoading}
                                    />
                                </div>

                                {priceAccessError && (
                                    <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                                        {priceAccessError}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={priceAccessLoading}
                                    className="w-full rounded-md px-4 py-3 text-sm font-black uppercase tracking-widest text-[var(--color-accent-dark)] shadow-lg transition-opacity disabled:opacity-60"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    {priceAccessLoading ? 'Validando...' : 'Ingresar a la tienda'}
                                </button>
                            </form>
                        </div>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="py-20 text-center text-[var(--text-muted)] flex flex-col items-center">
                        <Package size={48} className="mb-4 opacity-20" />
                        <p>No encontramos productos con ese criterio.</p>
                        <button onClick={() => { setSearchTerm(''); setSelectedCategory('Todos') }} className="mt-4 font-bold text-sm hover:underline" style={{ color: primaryColor }}>Ver todo</button>
                    </div>
                ) : (
                    <div className={config.view_mode === 'list' 
                        ? "flex flex-col gap-4" 
                        : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"}>
                        {filteredProducts.map(product => {
                            const isList = config.view_mode === 'list';
                            return (
                            <div
                                key={product.id}
                                onClick={() => setSelectedProduct(product)}
                                className={`rounded-md shadow-sm border overflow-hidden cursor-pointer hover:shadow-md transition-all group relative ${isList ? 'flex flex-row h-32 md:h-40' : 'flex flex-col'}`}
                                style={{ backgroundColor: cardBg, borderColor: cardBorder, color: cardText }}
                            >
                                <div className={`${isList ? 'w-32 md:w-40 h-full' : 'aspect-square'} bg-[var(--surface-2)] flex items-center justify-center relative overflow-hidden shrink-0`}>
                                    {product.image_url ? (
                                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    ) : (
                                        <img src="https://placehold.co/400x400/f8fafc/94a3b8?text=Sin+Imagen" alt="Sin imagen" className="w-full h-full object-cover opacity-50 group-hover:scale-110 transition-transform" />
                                    )}
                                    {/* Badge Agotado */}
                                    {!allowCheckoutWithoutStock && product.stock <= 0 && (
                                        <div className="absolute inset-0 bg-[var(--surface)]/80 flex items-center justify-center backdrop-blur-sm z-10">
                                            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">Agotado</span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-3 flex flex-col flex-grow">
                                    <h3 className="font-bold text-sm leading-tight mb-1 line-clamp-2" style={{ color: cardText }}>{product.name}</h3>
                                    <p className="text-xs line-clamp-1 mb-3" style={{ color: withAlpha(cardText, 'B3') }}>{product.description || product.categoryName}</p>
                                    {config.show_stock && (
                                        <div className="text-[10px] font-bold text-left mb-1 mt-auto" style={{ color: withAlpha(cardText, 'B3') }}>
                                            Stock: {product.stock}
                                        </div>
                                    )}
                                    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 ${!config.show_stock ? 'mt-auto' : ''}`}>
                                        <span className="font-black text-sm sm:text-base leading-tight" style={{ color: cardText }}>
                                            {showStorePrices ? formatCurrency(product.price) : 'Consultar'}
                                        </span>
                                        {(() => {
                                            const cartItems = cart.filter(c => c.id === product.id);
                                            const totalQty = cartItems.reduce((acc, item) => acc + item.qty, 0);
                                            const hasVariants = product.variants && product.variants.length > 0;

                                            // Si ya está en el carrito, mostramos los controles +/- rápidos (sin importar variantes, para el 1er item agregado)
                                            if (totalQty > 0) {
                                                const quickItem = cartItems.length === 1 ? cartItems[0] : null;
                                                return (
                                                    <div className="flex items-center self-end sm:self-auto rounded-md md:rounded-full text-white shadow-md text-xs sm:text-sm transition-all" style={{ backgroundColor: config.theme_config?.primaryColor || (isGastronomy ? '#f97316' : '#0f172a') }} onClick={(e) => e.stopPropagation()}>
                                                        <button onClick={() => updateQty(cartItems[0].cartItemId, -1)} className="w-7 h-7 md:w-10 md:h-10 flex items-center justify-center hover:bg-black/20 rounded-l-md md:rounded-l-full transition-colors"><Minus size={14} className="md:w-4 md:h-4"/></button>
                                                        {quickItem ? (
                                                            <input
                                                                type="text"
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                value={qtyDrafts[quickItem.cartItemId] ?? String(quickItem.qty)}
                                                                onChange={(e) => handleQtyDraftChange(quickItem.cartItemId, e.target.value)}
                                                                onBlur={() => commitQtyDraft(quickItem.cartItemId)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') e.currentTarget.blur();
                                                                }}
                                                                className="w-7 md:w-10 bg-transparent text-center font-black outline-none text-xs md:text-sm"
                                                            />
                                                        ) : (
                                                            <span className="px-1.5 sm:px-2 font-black min-w-[1.5rem] sm:min-w-[2rem] text-center">{totalQty}</span>
                                                        )}
                                                        <button onClick={() => updateQty(cartItems[0].cartItemId, 1)} className="w-7 h-7 md:w-10 md:h-10 flex items-center justify-center hover:bg-black/20 rounded-r-md md:rounded-r-full transition-colors"><Plus size={14} className="md:w-4 md:h-4"/></button>
                                                    </div>
                                                );
                                            }
                                            
                                            // Si no hay stock, mostramos un botón deshabilitado
                                            if (!allowCheckoutWithoutStock && product.stock <= 0) {
                                                return (
                                                    <button
                                                        disabled
                                                        className="w-7 h-7 md:w-10 md:h-10 self-end sm:self-auto rounded-md md:rounded-full flex items-center justify-center bg-gray-300 text-[var(--text-muted)] cursor-not-allowed transition-all shadow-sm"
                                                    >
                                                        <Plus size={16} />
                                                    </button>
                                                );
                                            }

                                            return (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Siempre agregar directo al carrito de manera rápida
                                                        // Si tiene variantes, usar la primera configuración por defecto
                                                        const variantToUse = hasVariants ? product.variants[0] : null;
                                                        addToCart(product, 1, '', variantToUse);
                                                    }}
                                                    style={{ backgroundColor: config.theme_config?.primaryColor || (isGastronomy ? '#f97316' : '#0f172a') }}
                                                    className="w-7 h-7 md:w-10 md:h-10 self-end sm:self-auto rounded-md md:rounded-full flex items-center justify-center text-white transition-all shadow-md hover:scale-110 hover:shadow-lg">
                                                    <Plus size={16} />
                                                </button>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )})}
                    </div>
                )}
            </main>
            )}

            {/* FOOTER & REDES */}
            <footer className="border-t py-12 mt-10" style={{ borderColor: cardBorder, backgroundColor: cardBg }}>
                <div className="max-w-4xl mx-auto px-4 flex flex-col items-center justify-center text-center">
                    
                    <h4 className="font-black text-xl mb-2" style={{ color: cardText }}>{config.store_title}</h4>
                    <p className="text-sm max-w-md" style={{ color: withAlpha(cardText, 'B3') }}>{isGastronomy ? 'Delivery rápido y seguro.' : 'Tu tienda de confianza, compras 100% seguras.'}</p>
                    
                    <div className="flex items-center justify-center gap-4 mt-8">
                        {config.instagram && (
                            <a href={`https://instagram.com/${config.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center text-pink-600 hover:bg-pink-100 hover:scale-110 transition-all shadow-sm">
                                <Instagram size={24} />
                            </a>
                        )}
                        {config.whatsapp && (
                            <a href={`https://wa.me/${config.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-[var(--color-accent-soft)] flex items-center justify-center text-[var(--color-accent-dark)] hover:bg-[var(--color-accent-soft)] hover:scale-110 transition-all shadow-sm">
                                <Phone size={24} />
                            </a>
                        )}
                    </div>
                    
                </div>
                
                <div className="border-t mt-12 pt-6 max-w-4xl mx-auto px-4 flex flex-col md:flex-row items-center justify-center gap-4" style={{ borderColor: cardBorder }}>
                    <p className="text-xs font-bold" style={{ color: withAlpha(cardText, '99') }}>© 2026 {config.store_title}. Potenciado por Gastify.</p>
                </div>
            </footer>

            {/* BOTÓN WHATSAPP FLOTANTE */}
            {/* --- MODALES --- */}

            {/* 1. DETALLE DE PRODUCTO */}
            {selectedProduct && (
                <ProductDetailModal
                    product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                    onAdd={addToCart}
                    theme={config.theme_config}
                    storeType={config.store_type}
                    config={config}
                    primaryColor={primaryColor}
                    allowCheckoutWithoutStock={allowCheckoutWithoutStock}
                    showPrices={showStorePrices}
                />
            )}

            {/* 2. CARRITO (DRAWER) */}
            {isCartOpen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setIsCartOpen(false)} />
                    <div className="relative w-full max-w-md bg-[var(--surface)] h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
                        <div className="p-4 border-b flex items-center justify-between bg-[var(--surface-2)]">
                            <h2 className="font-bold text-lg flex items-center gap-2"><ShoppingBag size={20} /> Tu Pedido</h2>
                            <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {cart.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-[var(--text-muted)]">
                                    <ShoppingCart size={64} className="mb-4 opacity-20" />
                                    <p>Tu carrito está vacío</p>
                                    <button onClick={() => setIsCartOpen(false)} className="mt-4 px-6 py-2 bg-slate-900 text-white rounded-full text-sm font-bold">Ver Productos</button>
                                </div>
                            ) : (
                                cart.map(item => (
                                    <div key={item.cartItemId} className="flex gap-4 p-3 bg-[var(--surface)] border border-gray-100 rounded-xl shadow-sm">
                                        <div className="w-16 h-16 bg-[var(--surface-2)] rounded-lg flex items-center justify-center text-2xl shrink-0 overflow-hidden">
                                            {item.image_url ? (
                                                <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span>{isGastronomy ? '🍔' : '📦'}</span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start mb-1">
                                                <h4 className="font-bold text-sm text-[var(--color-accent-dark)] truncate">{sanitizeProductDisplayName(item.name || 'Producto')}</h4>
                                                <span className="font-bold text-sm">
                                                    {showStorePrices ? formatCurrency(item.price * item.qty) : 'Precio oculto'}
                                                </span>
                                            </div>
                                            {getCleanVariantLabel(item.variantName) && <div className="text-xs text-[var(--text-muted)] mb-1 bg-[var(--surface-2)] inline-block px-1.5 py-0.5 rounded">{getCleanVariantLabel(item.variantName)}</div>}
                                            {item.note && <div className="text-xs text-orange-600 italic mb-2">"{item.note}"</div>}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center border rounded-lg h-8">
                                                    <button onClick={() => updateQty(item.cartItemId, -1)} className="px-2 h-full hover:bg-[var(--surface-2)] border-r"><Minus size={12} /></button>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        value={qtyDrafts[item.cartItemId] ?? String(item.qty)}
                                                        onChange={(e) => handleQtyDraftChange(item.cartItemId, e.target.value)}
                                                        onBlur={() => commitQtyDraft(item.cartItemId)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') e.currentTarget.blur();
                                                        }}
                                                        className="w-10 text-center text-sm font-bold outline-none bg-transparent"
                                                    />
                                                    <button onClick={() => updateQty(item.cartItemId, 1)} className="px-2 h-full hover:bg-[var(--surface-2)] border-l"><Plus size={12} /></button>
                                                </div>
                                                <button onClick={() => removeFromCart(item.cartItemId)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {cart.length > 0 && (
                            <div className="p-4 border-t bg-[var(--surface-2)]">
                                {lowStockCartItems.length > 0 && (
                                    <div className="mb-4">
                                        <LowStockNotice
                                            items={lowStockCartItems}
                                            message={lowStockAlertConfig.message}
                                            storeConfig={config}
                                            compact
                                        />
                                    </div>
                                )}
                                <div className="flex justify-between items-end mb-4">
                                    <span className="text-[var(--text-muted)] text-sm font-medium">{showStorePrices ? 'Total Estimado' : 'Importes'}</span>
                                    <span className="text-2xl font-black text-[var(--text-default)]">
                                        {showStorePrices ? formatCurrency(cartTotal) : 'Ocultos'}
                                    </span>
                                </div>
                                {storeStatus.isOpen || config.always_open ? (
                                    <button
                                        onClick={() => { setIsCartOpen(false); setCheckoutOpen(true); }}
                                        style={{ backgroundColor: config.theme_config?.primaryColor || (isGastronomy ? '#ea580c' : '#0f172a') }}
                                        className="w-full py-4 text-white rounded-md font-bold text-sm hover:opacity-90 transition-colors shadow-lg flex items-center justify-center gap-2"
                                    >
                                        Iniciar Compra <ArrowRight size={18} />
                                    </button>
                                ) : (
                                    <div className="w-full py-3 bg-[var(--surface-2)] text-[var(--text-muted)] rounded-md font-bold text-sm text-center flex items-center justify-center gap-2 cursor-not-allowed">
                                        <Clock size={16} /> Tienda Cerrada
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 3. CHECKOUT REAL (MODAL) */}
            {checkoutOpen && (
                <CheckoutModal
                    onClose={() => setCheckoutOpen(false)}
                    cartItems={cart}
                    orgId={config.organization_id}
                    config={config}
                    primaryColor={primaryColor}
                    siteId={siteId}
                    enabledPaymentMethods={enabledPaymentMethods}
                    showPrices={showStorePrices}
                    onClientResolved={(client) => {
                        if (!client) return;
                        setActiveStoreClient(client);
                        if (requiresCuitForPriceView) persistStorePriceAccess(client);
                    }}
                    onSuccess={(code) => {
                        setCart([]);
                        setCheckoutOpen(false);
                        toast.success(`Pedido recibido: ${code}`, { duration: 6000 });
                        navigate(`/rastreo/${code}?new=true`);
                    }}
                />
            )}

            <style>{`
            .no-scrollbar::-webkit-scrollbar { display: none; }
            .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
        </div>
    );
}
